import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiSave,
  FiSearch,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { AdminAffiliate, adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { showToast } from '../../utils/toast';

type AffiliateDraft = {
  enabled: boolean;
  clientAccess: boolean;
  oneTimeCommissionPercent: string;
  recurringCommissionPercent: string;
};

const formatMoney = (cents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((Number(cents) || 0) / 100);

const resolveOneTime = (affiliate: AdminAffiliate) =>
  Number(
    affiliate.affiliateOneTimeCommissionPercent ?? affiliate.affiliateCommissionPercent ?? 0
  );

const resolveRecurring = (affiliate: AdminAffiliate) =>
  Number(
    affiliate.affiliateRecurringCommissionPercent ?? affiliate.affiliateCommissionPercent ?? 0
  );

const AdminAffiliateManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<string, AffiliateDraft>>({});
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [selectedPayoutDetails, setSelectedPayoutDetails] = useState<
    Awaited<ReturnType<typeof adminApi.getAffiliatePayoutDetails>> | null
  >(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'affiliates'],
    queryFn: () => adminApi.getAffiliates(),
  });

  const affiliates = useMemo<AdminAffiliate[]>(
    () => data?.affiliates || [],
    [data?.affiliates],
  );

  useEffect(() => {
    const next: Record<string, AffiliateDraft> = {};
    for (const affiliate of affiliates) {
      next[affiliate._id] = {
        enabled: !!affiliate.ableToAffiliate,
        clientAccess: affiliate.affiliateClientAccess !== false,
        oneTimeCommissionPercent: String(resolveOneTime(affiliate)),
        recurringCommissionPercent: String(resolveRecurring(affiliate)),
      };
    }
    setDrafts(next);
  }, [affiliates]);

  const filteredAffiliates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return affiliates;
    return affiliates.filter(
      (affiliate) =>
        affiliate.name.toLowerCase().includes(term) ||
        affiliate.email.toLowerCase().includes(term),
    );
  }, [affiliates, search]);

  useEffect(() => setPage(1), [search]);

  const totalPages = Math.max(1, Math.ceil(filteredAffiliates.length / PAGE_SIZE));
  const paginatedAffiliates = filteredAffiliates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const enabledAffiliates = affiliates.filter((affiliate) => affiliate.ableToAffiliate);
  const totalEarned = enabledAffiliates.reduce(
    (sum, affiliate) => sum + affiliate.totalEarnedCents,
    0,
  );
  const totalPending = enabledAffiliates.reduce(
    (sum, affiliate) => sum + affiliate.pendingCents,
    0,
  );

  const saveMutation = useMutation({
    mutationFn: ({
      userId,
      enabled,
      clientAccess,
      oneTimeCommissionPercent,
      recurringCommissionPercent,
    }: {
      userId: string;
      enabled: boolean;
      clientAccess: boolean;
      oneTimeCommissionPercent: number;
      recurringCommissionPercent: number;
    }) =>
      adminApi.updateAffiliate(userId, {
        enabled,
        clientAccess,
        oneTimeCommissionPercent,
        recurringCommissionPercent,
        commissionPercent: oneTimeCommissionPercent,
      }),
    onSuccess: (response) => {
      showToast('success', response.message || 'Affiliate settings saved');
      queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
    },
    onError: (err: any) => {
      showToast(
        'error',
        err?.response?.data?.error || err.message || 'Failed to save affiliate settings',
      );
    },
  });

  const payoutMutation = useMutation({
    mutationFn: (userId: string) => adminApi.markAffiliatePaid(userId),
    onSuccess: (response) => {
      showToast('success', response.message || 'Commission marked as paid');
      queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
    },
    onError: (err: any) => {
      showToast(
        'error',
        err?.response?.data?.error || err.message || 'Failed to update payout',
      );
    },
  });

  const payoutDetailsMutation = useMutation({
    mutationFn: (userId: string) => adminApi.getAffiliatePayoutDetails(userId),
    onSuccess: (response) => setSelectedPayoutDetails(response),
    onError: (err: any) => {
      showToast(
        'error',
        err?.response?.data?.error || err.message || 'Failed to load payout details',
      );
    },
  });

  const closePayoutDetails = () => {
    setSelectedPayoutDetails(null);
    payoutDetailsMutation.reset();
  };

  const saveAffiliate = (affiliate: AdminAffiliate) => {
    const draft = drafts[affiliate._id];
    const oneTimeCommissionPercent = Number(draft?.oneTimeCommissionPercent);
    const recurringCommissionPercent = Number(draft?.recurringCommissionPercent);
    if (
      !Number.isFinite(oneTimeCommissionPercent) ||
      oneTimeCommissionPercent < 0 ||
      oneTimeCommissionPercent > 100
    ) {
      showToast('error', 'One-time commission % must be between 0 and 100');
      return;
    }
    if (
      !Number.isFinite(recurringCommissionPercent) ||
      recurringCommissionPercent < 0 ||
      recurringCommissionPercent > 100
    ) {
      showToast('error', 'Recurring commission % must be between 0 and 100');
      return;
    }
    saveMutation.mutate({
      userId: affiliate._id,
      enabled: !!draft?.enabled,
      clientAccess: draft?.clientAccess !== false,
      oneTimeCommissionPercent,
      recurringCommissionPercent,
    });
  };

  if (isLoading) return <Loader message="Loading affiliate accounts..." />;
  if (error) return <ErrorDisplay error="Failed to load affiliate accounts" />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-foreground">
          <FiUsers className="h-6 w-6 text-primary shrink-0" />
          Affiliate Management
        </h2>
        <p className="mt-1 text-sm text-muted">
          Grant affiliate access, set one-time / recurring commission rates (after 3% fee), and
          record payouts.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-surface p-4">
          <div className="flex items-center gap-2 text-sm text-muted">
            <FiUsers className="text-primary" /> Active affiliates
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{enabledAffiliates.length}</p>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-4">
          <div className="flex items-center gap-2 text-sm text-muted">
            <FiDollarSign className="text-emerald-500" /> Total earned
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{formatMoney(totalEarned)}</p>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-4">
          <div className="flex items-center gap-2 text-sm text-muted">
            <FiClock className="text-amber-500" /> Pending payout
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{formatMoney(totalPending)}</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search users by name or email"
          className="w-full rounded-xl border border-border bg-surface py-2.5 pl-10 pr-4 text-sm text-foreground outline-none transition focus:border-primary"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-surface min-w-0">
        <div className="table-scroll">
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead className="border-b border-border bg-background/60 text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Access</th>
                <th className="px-4 py-3">Client mode</th>
                <th className="px-4 py-3">Commission</th>
                <th className="px-4 py-3">Referrals</th>
                <th className="px-4 py-3">Earned</th>
                <th className="px-4 py-3">Withdrawn</th>
                <th className="px-4 py-3">Pending</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedAffiliates.map((affiliate) => {
                const draft = drafts[affiliate._id] || {
                  enabled: false,
                  clientAccess: true,
                  oneTimeCommissionPercent: '0',
                  recurringCommissionPercent: '0',
                };
                const isSaving =
                  saveMutation.isPending && saveMutation.variables?.userId === affiliate._id;
                const isPaying =
                  payoutMutation.isPending && payoutMutation.variables === affiliate._id;

                return (
                  <tr key={affiliate._id} className="hover:bg-background/40">
                    <td className="px-4 py-4">
                      <p className="font-semibold text-foreground">{affiliate.name}</p>
                      <p className="text-xs text-muted">{affiliate.email}</p>
                    </td>
                    <td className="px-4 py-4">
                      <label className="inline-flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={draft.enabled}
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [affiliate._id]: {
                                ...draft,
                                enabled: event.target.checked,
                              },
                            }))
                          }
                          className="h-4 w-4 accent-primary"
                        />
                        <span className={draft.enabled ? 'text-emerald-500' : 'text-muted'}>
                          {draft.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-4">
                      <label
                        className={`inline-flex items-center gap-2 ${
                          draft.enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                        }`}
                        title={
                          draft.enabled
                            ? 'Allow this affiliate to switch into Client mode'
                            : 'Enable affiliate access first'
                        }
                      >
                        <input
                          type="checkbox"
                          checked={draft.clientAccess}
                          disabled={!draft.enabled}
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [affiliate._id]: {
                                ...draft,
                                clientAccess: event.target.checked,
                              },
                            }))
                          }
                          className="h-4 w-4 accent-primary"
                        />
                        <span
                          className={
                            draft.clientAccess && draft.enabled ? 'text-primary' : 'text-muted'
                          }
                        >
                          {draft.clientAccess ? 'Allowed' : 'Blocked'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-2 min-w-[150px]">
                        <label className="flex items-center gap-2">
                          <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted">
                            1-time
                          </span>
                          <div className="flex items-center">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={draft.oneTimeCommissionPercent}
                              onChange={(event) =>
                                setDrafts((current) => ({
                                  ...current,
                                  [affiliate._id]: {
                                    ...draft,
                                    oneTimeCommissionPercent: event.target.value,
                                  },
                                }))
                              }
                              className="w-16 rounded-l-lg border border-border bg-background px-2 py-1.5 text-foreground outline-none focus:border-primary"
                            />
                            <span className="rounded-r-lg border border-l-0 border-border bg-background px-2 py-1.5 text-muted">
                              %
                            </span>
                          </div>
                        </label>
                        <label className="flex items-center gap-2">
                          <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted">
                            Recur
                          </span>
                          <div className="flex items-center">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={draft.recurringCommissionPercent}
                              onChange={(event) =>
                                setDrafts((current) => ({
                                  ...current,
                                  [affiliate._id]: {
                                    ...draft,
                                    recurringCommissionPercent: event.target.value,
                                  },
                                }))
                              }
                              className="w-16 rounded-l-lg border border-border bg-background px-2 py-1.5 text-foreground outline-none focus:border-primary"
                            />
                            <span className="rounded-r-lg border border-l-0 border-border bg-background px-2 py-1.5 text-muted">
                              %
                            </span>
                          </div>
                        </label>
                        <p className="text-[10px] text-muted leading-snug">
                          Formula: (payment − 3%) × rate
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-foreground">{affiliate.referralCount}</td>
                    <td className="px-4 py-4 font-semibold text-foreground">
                      {formatMoney(affiliate.totalEarnedCents)}
                    </td>
                    <td className="px-4 py-4 text-emerald-500">
                      {formatMoney(affiliate.withdrawnCents)}
                    </td>
                    <td className="px-4 py-4 text-amber-500">
                      {formatMoney(affiliate.pendingCents)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => saveAffiliate(affiliate)}
                          disabled={isSaving}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                        >
                          <FiSave /> {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => payoutDetailsMutation.mutate(affiliate._id)}
                          disabled={!affiliate.hasPayoutDetails || payoutDetailsMutation.isPending}
                          title={
                            affiliate.hasPayoutDetails
                              ? 'View payout details'
                              : 'No payout details saved'
                          }
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FiCreditCard />
                          {payoutDetailsMutation.isPending &&
                          payoutDetailsMutation.variables === affiliate._id
                            ? 'Loading...'
                            : 'View details'}
                        </button>
                        <button
                          onClick={() => payoutMutation.mutate(affiliate._id)}
                          disabled={affiliate.pendingCents <= 0 || isPaying}
                          title="Record all pending commission as paid"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-foreground transition hover:bg-background disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FiCheckCircle />
                          {isPaying ? 'Updating...' : 'Mark paid'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredAffiliates.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-muted">No matching users found.</p>
        )}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-border px-4 py-3">
            <p className="text-xs text-muted">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredAffiliates.length)} of {filteredAffiliates.length}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg p-2 text-muted transition hover:bg-background hover:text-foreground disabled:opacity-40"
              >
                <FiChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`min-w-[32px] rounded-lg px-2 py-1 text-xs font-semibold transition ${
                    n === page
                      ? 'bg-primary text-white'
                      : 'text-muted hover:bg-background hover:text-foreground'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-lg p-2 text-muted transition hover:bg-background hover:text-foreground disabled:opacity-40"
              >
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedPayoutDetails && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-surface-elevated shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-border p-4 sm:p-5">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 text-lg font-bold text-foreground">
                  <FiCreditCard className="text-primary" />
                  Payout Details
                </h3>
                <p className="mt-1 text-xs text-muted break-anywhere">
                  {selectedPayoutDetails.user.name} · {selectedPayoutDetails.user.email}
                </p>
              </div>
              <button
                type="button"
                onClick={closePayoutDetails}
                className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-foreground shrink-0"
                aria-label="Close payout details"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-5">
              <div className="rounded-xl border border-border bg-surface p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">
                  Bank / PayPal details
                </p>
                <p className="mt-3 whitespace-pre-wrap break-words break-anywhere text-sm leading-6 text-foreground">
                  {selectedPayoutDetails.payoutDetails.details || '—'}
                </p>
              </div>
            </div>

            <div className="flex justify-end border-t border-border p-4 sm:p-5">
              <button
                type="button"
                onClick={closePayoutDetails}
                className="rounded-lg bg-primary px-5 py-2.5 text-xs font-semibold text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliateManagement;
